var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ApolloClient, createHttpLink, from, ApolloLink, } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import ApolloLinkTimeout from 'apollo-link-timeout';
import { AsyncStorageWrapper, CachePersistor } from 'apollo3-cache-persist';
import { Platform } from 'react-native';
import { Metric, Trace } from '../metrics';
import config from './config';
import TENANT_ID from '../config/TenantId';
import { toErrorMessage } from '../util/ErrorHelper';
import Debug from '../util/Debug';
import fetchAllDataQuery from './allDataQuery';
export default class API {
    constructor() {
        const delegatorURL = `${API.apiConfig.baseUrl}/${config.ConcertClient.ENDPOINT}`;
        const authenticationLink = setContext((_, { headers }) => __awaiter(this, void 0, void 0, function* () {
            // We are supplying both cookie and Authorization header,
            // AtoZDelegator always takes cookie on priority,
            // if setting cookie on request fails then authorization will be used
            const token = yield API.apiConfig.getAccessToken();
            if (!token && (Platform.OS !== 'web')) {
                throw Error('Failed to retrieve access token for graphql request');
            }
            try {
                yield API.apiConfig.refreshCookie(delegatorURL);
            }
            catch (error) {
                const concertErrorData = {
                    message: 'failed to refresh cookie for concert',
                    error: toErrorMessage(error),
                };
                Metric('ConcertAuthCookieRefreshError', TENANT_ID, {
                    attributes: concertErrorData,
                }).put();
                Debug.error(concertErrorData.message, concertErrorData.error);
            }
            return {
                headers: Object.assign(Object.assign({}, headers), { 'x-atoz-client-id': config.ConcertClient.SERVICE_ID, Authorization: token ? `Bearer ${token}` : '' }),
                uri: `${delegatorURL}?employeeId=${API.apiConfig.employeeId}`,
            };
        }));
        const errorLink = onError(({ graphQLErrors, networkError }) => {
            if (graphQLErrors) {
                graphQLErrors.forEach(({ message, locations, path }) => {
                    const concertErrorData = {
                        message: 'concert graphQL errors',
                        error: `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
                    };
                    Metric('ConcertGraphQLErrorsCommon', TENANT_ID, {
                        attributes: concertErrorData,
                    }).put();
                    Debug.error(concertErrorData);
                });
            }
            if (networkError) {
                const concertNetworkErrorData = {
                    message: 'concert network error',
                    error: toErrorMessage(networkError),
                };
                Metric('ConcertNetworkErrorsCommon', TENANT_ID, {
                    attributes: concertNetworkErrorData,
                }).put();
                Debug.error(concertNetworkErrorData);
            }
        });
        const latencyLink = new ApolloLink((operation, forward) => {
            // Called before operation is sent to server
            const traceConcert = Trace('ConcertRequestLoadCommon', TENANT_ID);
            traceConcert.mergeAttributes({ operationName: operation.operationName });
            return forward(operation).map((data) => {
                // Called after server responds
                traceConcert.log();
                return data;
            });
        });
        // delay will be exponentially increased with each retry
        // jitter will randomly adjust the delay between 0ms and 2x of current delay
        const retryLink = new RetryLink({
            delay: {
                initial: 300,
                max: Infinity,
                jitter: true,
            },
            attempts: {
                max: 3,
            },
        });
        const timeoutLink = new ApolloLinkTimeout(config.ConcertClient.DEFAULT_TIMEOUT);
        const httpLink = createHttpLink({ credentials: 'include' });
        this.concertClient = new ApolloClient({
            uri: delegatorURL,
            link: from([
                retryLink, authenticationLink, errorLink, latencyLink, timeoutLink, httpLink,
            ]),
            cache: API.cacheProvider,
            queryDeduplication: true,
            defaultOptions: {
                watchQuery: {
                    fetchPolicy: 'cache-first',
                },
                query: {
                    fetchPolicy: 'cache-first',
                },
            },
        });
    }
    /**
     * Call once (possibly at the start of app) to configure Concert API client,
     * @param apiConfig {@link APIConfig} initial configuration for API
     */
    static configure(apiConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            API.apiConfig = apiConfig;
            if (Platform.OS === 'web') {
                API.cacheProvider = new InMemoryCache();
                return;
            }
            yield API.restoreCache();
            API.startPolling();
            apiConfig.onAppForeground(API.startPolling);
            apiConfig.onAppBackground(API.stopPolling);
        });
    }
    /**
     * after configuration is called, use this method to change employee id to set spoofing session
     * Note - doesn't verify if authenticatedEmployeeId has permissions to start spoofing session
     * backend will reject the call in case user doesn't have permission to spoof
     * @param _employeeId authenticatedEmployeeId | spoofedEmployeeId
     */
    static setEmployeeId(_employeeId) {
        API.apiConfig.employeeId = _employeeId;
    }
    static getConcertClient() {
        if (!API.instance) {
            API.instance = new API();
        }
        return API.instance.concertClient;
    }
    static startPolling() {
        Debug.log('Started concert cache update service');
        if (!API.updateInterval) {
            API.updateInterval = setInterval(API.updateCacheData, config.ConcertClient.INTERVAL_TO_CACHE_REFRESH);
        }
    }
    static stopPolling() {
        Debug.log('Stopped concert cache update service');
        if (API.updateInterval) {
            clearInterval(API.updateInterval);
            API.updateInterval = null;
        }
    }
    static terminate() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const client = this.getConcertClient();
                yield client.clearStore();
                yield this.cacheProvider.reset();
                this.cacheProvider.gc();
                const provider = new AsyncStorageWrapper(API.apiConfig.storageProvider);
                const persistor = new CachePersistor({
                    cache: API.cacheProvider,
                    storage: provider,
                    key: 'concert-cache-storage',
                    debug: __DEV__,
                });
                yield API.purgeDiskCache(persistor);
                client.stop();
            }
            catch (error) {
                Debug.error('Failed to clear terminate Apollo API', error);
                Metric('APITerminateError', TENANT_ID, {
                    attributes: {
                        error: toErrorMessage(error),
                    },
                }).putAndLog();
            }
        });
    }
    /**
     * Spawns a network request to fetch all data from concert every 15 min until app is active
     */
    static updateCacheData() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!API.apiConfig.employeeId)
                return;
            const traceUpdateQuery = Trace('UpdateConcertCache30SecTimeout', TENANT_ID);
            // To avoid backend from overwhelming with retries, we relax the timeout
            // when call fails with low network connection the apollo will internally retry 3 times
            try {
                yield API.getConcertClient().query({
                    query: fetchAllDataQuery(API.apiConfig.employeeId),
                    fetchPolicy: 'network-only',
                    context: { timeout: config.ConcertClient.CACHE_REFRESH_TIMEOUT },
                });
                traceUpdateQuery.mergeAttributes({ resultType: 'Success' });
            }
            catch (error) {
                traceUpdateQuery.mergeAttributes({ resultType: 'Failure' });
                Metric('UpdateConcertCacheError', TENANT_ID, {
                    attributes: {
                        error: toErrorMessage(error),
                    },
                }).put();
                Debug.error('Failed updating concert cache', error);
            }
            traceUpdateQuery.log();
        });
    }
    static purgeDiskCache(persistor) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield persistor.purge();
                Metric('ConcertDiskCachePurgeSuccess', TENANT_ID).putAndLog();
            }
            catch (error) {
                Metric('ConcertDiskCachePurgeFailed', TENANT_ID, {
                    attributes: {
                        error: toErrorMessage(error),
                    },
                }).putAndLog();
            }
        });
    }
    static restoreCache() {
        return __awaiter(this, void 0, void 0, function* () {
            // we want to avoid accidental cache restore
            if (API.cacheProvider) {
                Metric('CacheProviderSet', TENANT_ID).putAndLog();
                return;
            }
            API.cacheProvider = new InMemoryCache();
            // typescript type-check issue https://github.com/apollographql/apollo-cache-persist/issues/55
            // prettier-ignore
            const provider = new AsyncStorageWrapper(API.apiConfig.storageProvider);
            try {
                const persistor = new CachePersistor({
                    cache: API.cacheProvider,
                    storage: provider,
                    key: 'concert-cache-storage',
                    debug: __DEV__,
                });
                if (API.apiConfig.shouldPurgeCache) {
                    yield API.purgeDiskCache(persistor);
                }
                else {
                    yield persistor.restore();
                }
            }
            catch (error) {
                Metric('ConcertDiskCacheRestoreFailed', TENANT_ID, {
                    attributes: {
                        error: toErrorMessage(error),
                    },
                }).putAndLog();
            }
            // silently call update cache
            API.updateCacheData();
        });
    }
}
//# sourceMappingURL=api.js.map